import { useTranslation } from 'react-i18next';
import { DownloadIcon } from '@/shared/ui/icons';
import { cn } from '@/shared/utils/cn';
import type { ApiSupportMessage } from '@/shared/types/api/support';

type Props = {
  message: ApiSupportMessage;
};

const formatTime = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

export const WidgetsSupportChatMessageBubble = ({ message }: Props) => {
  const { t } = useTranslation('support');
  const isOwn = message.sender_type === 'USER';

  return (
    <div className={cn('flex w-full', isOwn ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'flex max-w-[80%] flex-col gap-1 rounded-2xl px-3 py-2 shadow-sm',
          isOwn
            ? 'rounded-br-md bg-brand-600 text-white'
            : 'rounded-bl-md border border-slate-200 bg-white text-ink',
        )}
      >
        {message.file && message.file_type === 'IMAGE' && (
          <a href={message.file} target="_blank" rel="noreferrer" className="block">
            <img
              src={message.file}
              alt={message.file_name ?? t('imageAlt')}
              className="max-h-60 w-full rounded-lg object-cover"
            />
          </a>
        )}
        {message.file && message.file_type === 'FILE' && (
          <a
            href={message.file}
            target="_blank"
            rel="noreferrer"
            download={message.file_name ?? undefined}
            className={cn(
              'flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm transition-colors',
              isOwn ? 'bg-white/15 hover:bg-white/25' : 'bg-slate-100 hover:bg-slate-200',
            )}
          >
            <DownloadIcon className="h-4 w-4 shrink-0" aria-hidden />
            <span className="min-w-0 flex-1 truncate">{message.file_name ?? t('download')}</span>
          </a>
        )}
        {message.text && (
          <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">{message.text}</p>
        )}
        <span
          className={cn(
            'self-end text-[10px] leading-none',
            isOwn ? 'text-brand-100' : 'text-ink-faint',
          )}
        >
          {formatTime(message.created_at)}
        </span>
      </div>
    </div>
  );
};
